import DataTable from 'components/DataTable';
import Typography from 'components/Typography';
import Button from 'components/Button';
import Spinner from 'components/Spinner';
import toast from 'react-hot-toast';
import {useMemo, useCallback, useRef} from 'react';
import {format} from 'date-fns';
import {WebhookDelivery} from 'types';
import {useDeliveryHistory} from '../useDeliveryHistory';
import CopyIcon from '../../../icons/copy';
import styles from '../WebhookSettings.module.scss';

const STATUS_CLASS_MAP: Record<string, string> = {
  success: styles.statusBadgeSuccess,
  failed: styles.statusBadgeFailed,
  pending: styles.statusBadgePending,
  retrying: styles.statusBadgeRetrying,
};

function RetryIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
      <path d="M3 3v5h5" />
      <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
      <path d="M16 16h5v5" />
    </svg>
  );
}

function formatPayload(payload: string): string {
  try {
    return JSON.stringify(payload, null, 2);
  } catch {
    return payload;
  }
}

type DeliveryHistoryProps = {
  webhookId: string;
};

function DeliveryHistory({webhookId}: DeliveryHistoryProps) {
  const {
    deliveries,
    isLoadingDeliveries,
    DELIVERY_LIST_SCHEMA,
    retryDeliveryMutation,
    selectedDelivery,
    handleDeliveryClick,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useDeliveryHistory(webhookId);

  const scrollRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !hasNextPage || isFetchingNextPage) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 50;
    if (nearBottom) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const transformedData = useMemo(() => {
    return deliveries?.map((item: WebhookDelivery) => ({
      ...item,
      event_type: item.event_type,
      status: (
        <span
          className={`${styles.statusBadge} ${
            STATUS_CLASS_MAP[item.status] || ''
          }`}
        >
          {item.status}
        </span>
      ),
      response_status: item.response_status || '—',
      created_at: format(new Date(item.created_at), 'MMM d, yyyy HH:mm'),
      retry: (
        <div
          onClick={(e) => {
            e.stopPropagation();
            retryDeliveryMutation(item.id);
          }}
          className={styles.retryIcon}
          title="Retry delivery"
        >
          <RetryIcon />
        </div>
      ),
    }));
  }, [deliveries, retryDeliveryMutation]);

  const handleCopyPayload = () => {
    if (!selectedDelivery) return;
    navigator.clipboard.writeText(formatPayload(selectedDelivery.payload));
    toast.success('Payload copied to clipboard');
  };
  return (
    <div className={styles.deliveryContainer}>
      <div
        className={styles.deliveryListScroll}
        ref={scrollRef}
        onScroll={handleScroll}
      >
        <DataTable
          schema={DELIVERY_LIST_SCHEMA}
          data={transformedData || []}
          loading={isLoadingDeliveries}
          selectable={false}
          onClick={handleDeliveryClick}
          activeRowId={selectedDelivery?.id}
        />
        {isFetchingNextPage && (
          <div className={styles.loadMoreSpinner}>
            <Spinner />
          </div>
        )}
      </div>
      {selectedDelivery && (
        <div className={styles.deliveryDetail}>
          <div className={styles.deliveryDetailHeader}>
            <Typography variant="h3" style={{margin: 0}}>
              Delivery Detail
            </Typography>
            <Button
              color="outlined"
              size="sm"
              onClick={() => handleDeliveryClick({id: selectedDelivery.id})}
            >
              Close
            </Button>
          </div>

          <div className={styles.deliveryDetailRow}>
            <Typography variant="label" className={styles.detailLabel}>
              Payload
            </Typography>
            <div className={styles.jsonPayloadWrap}>
              <pre className={styles.jsonPayload}>
                <code>{formatPayload(selectedDelivery.payload)}</code>
              </pre>
              <CopyIcon
                width={14}
                height={14}
                className={styles.copyIcon}
                onClick={handleCopyPayload}
              />
            </div>
          </div>

          {selectedDelivery.response_body && (
            <div className={styles.deliveryDetailRow}>
              <Typography variant="label" className={styles.detailLabel}>
                Response Body
              </Typography>
              <pre className={styles.jsonPayload}>
                <code>{selectedDelivery.response_body}</code>
              </pre>
            </div>
          )}

          {selectedDelivery.last_failure_reason && (
            <div className={styles.deliveryDetailRow}>
              <Typography variant="label" className={styles.detailLabel}>
                Failure Reason
              </Typography>
              <Typography variant="p" className={styles.detailValue}>
                {selectedDelivery.last_failure_reason}
              </Typography>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DeliveryHistory;
